import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Panel, PanelHeader } from "@/components/ui/primitives";

/* The UI kit ships no section-level *error* component — its only error surface
   is the 404 page frame, which src/app/not-found.tsx already mirrors — so the
   error block is built from the kit's own primitives: elevation steps, the
   semantic error token and the kit's icon set, rather than a new visual
   language. An icon in a circular elevation-2 well, a bold 12px line, a subtle
   line under it, and an optional action.

   The kit does publish an empty state, as a responsive pair: `empty-state-large`
   and `empty-state-small`. EmptyBlock below is that pair — a bare brand-coloured
   icon with no well, 4px gaps, 16px padding, and type that steps 12px/12px up to
   17.5px/14px at the desktop breakpoint. The kit's node also carries
   bg/elevation-1; we deliberately leave it off, because callers sit inside Panel,
   which already paints that same token, and the ones that do not would gain a
   stray box. */

function StateBlock({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("flex flex-col items-center gap-[12px] px-[24px] py-[48px] text-center", className)}
      role="alert"
    >
      <span className="grid h-[38px] w-[38px] place-items-center rounded-full bg-error/12 text-error-fg">
        <Icon name={icon} size={20} />
      </span>

      <div className="flex max-w-[42ch] flex-col gap-[4px]">
        <p className="text-[12px] font-bold leading-[1.6] text-fg">{title}</p>
        {body && <p className="text-[12px] leading-[1.6] text-fg-subtle">{body}</p>}
      </div>

      {action}
    </div>
  );
}

function EmptyBlock({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon: IconName;
  title: string;
  body?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col items-center gap-[4px] p-[16px] text-center", className)}>
      <Icon name={icon} size={40} className="text-brand-bold lg:hidden" />
      <Icon name={icon} size={60} className="hidden text-brand-bold lg:block" />

      <div className="flex max-w-[42ch] flex-col gap-[4px]">
        <p className="text-[12px] font-bold leading-[1.6] text-fg lg:text-[17.5px]">{title}</p>
        {body && <p className="text-[12px] font-medium leading-[1.6] text-fg-muted lg:text-[14px]">{body}</p>}
      </div>

      {action}
    </div>
  );
}

/* A section whose data could not be loaded. Says what failed and what the
   reader can do, never "something went wrong". */
export function SectionError({
  title = "This section didn’t load",
  body = "The games service didn’t answer. Everything else on the page still works.",
  action,
  className,
}: {
  title?: string;
  body?: string;
  action?: ReactNode;
  className?: string;
}) {
  return <StateBlock icon="info" title={title} body={body} action={action} className={className} />;
}

/* A section that loaded correctly and has nothing in it. Distinct from an
   error on purpose: the dev environment returns empty leaderboards, and that
   is not a fault. */
export function SectionEmpty({
  title = "Nothing here yet",
  body,
  icon = "search",
  action,
  className,
}: {
  title?: string;
  body?: string;
  icon?: IconName;
  action?: ReactNode;
  className?: string;
}) {
  return <EmptyBlock icon={icon} title={title} body={body} action={action} className={className} />;
}

/* Panel-wrapped variants, for where a whole titled section is replaced. */
export function PanelState({
  icon,
  title,
  children,
}: {
  icon: IconName;
  title: string;
  children: ReactNode;
}) {
  return (
    <Panel>
      <PanelHeader icon={icon} title={title} />
      <div>{children}</div>
    </Panel>
  );
}

/* Loading placeholders that keep the lobby's rhythm instead of collapsing it,
   so nothing below jumps when the data arrives. */
export function TileSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("aspect-[158/209] w-full animate-pulse rounded-lg bg-elevation-2", className)} />
  );
}

export function RailWidgetSkeleton({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-lg bg-elevation-2", className)} />;
}

export function GameRowSkeleton({ tiles = 7, title }: { tiles?: number; title?: string }) {
  return (
    <Panel>
      <header className="relative flex h-[62px] items-center gap-[12px] px-[16px] py-[12px]">
        {title ? (
          <h2 className="text-[15.75px] font-black leading-[1.6] text-fg-subtle lg:text-[17.5px]">{title}</h2>
        ) : (
          <span className="h-[20px] w-[128px] animate-pulse rounded-md bg-elevation-2" />
        )}
        <span className="absolute inset-x-0 bottom-0 h-px bg-border" />
      </header>
      <div className="flex gap-[12px] overflow-hidden p-[16px]">
        {Array.from({ length: tiles }, (_, index) => (
          <div key={index} className="w-[158px] shrink-0">
            <TileSkeleton />
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function RowsSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="flex flex-col gap-[24px]">
      {Array.from({ length: rows }, (_, index) => (
        <GameRowSkeleton key={index} />
      ))}
    </div>
  );
}

export function HeroSkeleton({ className }: { className?: string }) {
  return <div className={cn("hero-wash h-[280px] w-full lg:h-[400px]", className)} />;
}

export function TitleSkeleton() {
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="h-[41px] w-[240px] max-w-full animate-pulse rounded-lg bg-elevation-1" />
      <div className="h-[17px] w-[140px] animate-pulse rounded-md bg-elevation-1" />
    </div>
  );
}

export function BlocksSkeleton({ blocks = 3, height = 120 }: { blocks?: number; height?: number }) {
  return (
    <>
      {Array.from({ length: blocks }, (_, index) => (
        <div
          key={index}
          className="w-full animate-pulse rounded-2xl bg-elevation-1"
          style={{ height }}
        />
      ))}
    </>
  );
}

function HeroHeadingSkeleton() {
  return (
    <div className="mx-auto flex max-w-[1096px] flex-col items-center gap-[12px] px-[24px] py-[32px] lg:px-[48px]">
      <div className="h-[44px] w-[280px] max-w-full animate-pulse rounded-lg bg-elevation-1" />
      <div className="h-[24px] w-full max-w-[60ch] animate-pulse rounded-md bg-elevation-1" />
    </div>
  );
}

export function PageSkeleton({ blocks = 3, height = 120 }: { blocks?: number; height?: number }) {
  return (
    <>
      <HeroSkeleton />
      <HeroHeadingSkeleton />
      <div className="mx-auto flex max-w-[1144px] flex-col gap-[16px] px-[16px] pb-[64px] lg:px-[24px]">
        <BlocksSkeleton blocks={blocks} height={height} />
      </div>
    </>
  );
}

export function GridSkeleton({ cards = 8 }: { cards?: number }) {
  return (
    <>
      <HeroSkeleton />
      <HeroHeadingSkeleton />
      <div className="mx-auto max-w-[1144px] px-[16px] pb-[64px] lg:px-[24px]">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: cards }, (_, index) => (
            <div key={index} className="h-[120px] w-full animate-pulse rounded-2xl bg-elevation-1" />
          ))}
        </div>
      </div>
    </>
  );
}

export function DocSkeleton() {
  return (
    <>
      <HeroSkeleton />
      <HeroHeadingSkeleton />
      <div className="mx-auto flex max-w-[1096px] flex-col gap-4 px-4 pb-10 lg:px-6">
        <div className="h-[560px] w-full animate-pulse rounded-2xl bg-elevation-1" />
      </div>
    </>
  );
}

export function StageSkeleton() {
  return (
    <div className="mx-auto flex max-w-[1144px] flex-col gap-[16px] px-[16px] py-[12px] lg:p-[24px]">
      <div className="aspect-[369/668] w-full animate-pulse rounded-t-2xl bg-elevation-1 sm:aspect-[1096/606]" />
      <div className="-mt-[16px] h-[78px] w-full animate-pulse rounded-b-2xl bg-elevation-1" />
      <div className="h-[86px] w-full animate-pulse rounded-2xl bg-elevation-1" />
      <GameRowSkeleton tiles={7} />
    </div>
  );
}
